import { useState, useCallback } from "react";

import { ListChecks, Check, ChevronDown, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";

import { cn } from "@/modern/utils";
import { Button } from "@/modern/components/ui/button-shadcn";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import {
  useCreateChecklistMutation,
  useCreateChecklistItemMutation,
} from "@/react/components/entityViews/checklist/api/checklist.api";
import { checklistEntityPathType } from "@/react/components/entityViews/checklist/api/checklist.query-keys";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedChecklist } from "../types";

interface ChecklistExtractionResultsProps {
  checklists: ExtractedChecklist[];
}

interface ChecklistCardState {
  expanded: boolean;
  selectedItems: Set<number>;
  created: boolean;
  processing: boolean;
}

export const ChecklistExtractionResults = ({ checklists }: ChecklistExtractionResultsProps) => {
  const { project, entityRecord } = useFileDetailModal();
  const createChecklistMutation = useCreateChecklistMutation();
  const createChecklistItemMutation = useCreateChecklistItemMutation();

  const [cardStates, setCardStates] = useState<Record<number, ChecklistCardState>>(() => {
    const initial: Record<number, ChecklistCardState> = {};
    checklists.forEach((checklist, index) => {
      initial[index] = {
        expanded: index === 0,
        selectedItems: new Set(checklist.items.map((_, i) => i)),
        created: false,
        processing: false,
      };
    });
    return initial;
  });

  const updateCardState = useCallback((index: number, update: Partial<ChecklistCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const toggleExpanded = useCallback((index: number) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], expanded: !prev[index].expanded },
    }));
  }, []);

  const toggleItemSelection = useCallback((checklistIndex: number, itemIndex: number) => {
    setCardStates((prev) => {
      const state = prev[checklistIndex];
      const newSelected = new Set(state.selectedItems);
      if (newSelected.has(itemIndex)) {
        newSelected.delete(itemIndex);
      } else {
        newSelected.add(itemIndex);
      }
      return {
        ...prev,
        [checklistIndex]: { ...state, selectedItems: newSelected },
      };
    });
  }, []);

  const handleCreateChecklist = useCallback(async (checklist: ExtractedChecklist, checklistIndex: number) => {
    if (!project || !entityRecord) return;

    const state = cardStates[checklistIndex];
    if (state?.created || state?.processing || state.selectedItems.size === 0) return;

    const effectiveBoardId = getSavedBoardSelection()?.id ?? null;
    if (effectiveBoardId == null) {
      toast.error("Unable to create checklist", { description: "No board selected." });
      return;
    }

    const entityFk = checklistEntityPathType(entityRecord.entityType);
    if (!entityFk) {
      toast.error("Unable to create checklist", {
        description: "This file is not attached to a task, resource, or client.",
      });
      return;
    }

    updateCardState(checklistIndex, { processing: true });

    try {
      const created = await createChecklistMutation.mutateAsync({
        boardId: effectiveBoardId,
        entityType: entityFk,
        entityId: entityRecord.id,
        body: {
          title: checklist.title || "Extracted Checklist",
          [entityFk]: entityRecord.id,
          archived: (entityRecord as { archived?: boolean }).archived ?? false,
          can_edit_grant: GRANTS.grant_five,
          can_view_grant: GRANTS.grant_six,
          assigned_boardusers: [],
          recurrence: "none",
        },
      });

      if (created?.id == null) throw new Error("missing checklist id");

      const selectedItems = checklist.items.filter((_, idx) => state.selectedItems.has(idx));
      for (let i = 0; i < selectedItems.length; i++) {
        await createChecklistItemMutation.mutateAsync({
          boardId: effectiveBoardId,
          checklistId: created.id,
          entityType: entityFk,
          entityId: entityRecord.id,
          body: {
            description: selectedItems[i].description,
            order: i,
          },
        });
      }

      updateCardState(checklistIndex, { created: true, processing: false });
      toast.success("Checklist Created", {
        description: `"${checklist.title || "Extracted Checklist"}" with ${selectedItems.length} item(s)`,
      });
    } catch {
      updateCardState(checklistIndex, { processing: false });
      toast.error("Failed to Create Checklist", {
        description: "Something went wrong. Please try again.",
      });
    }
  }, [
    project,
    entityRecord,
    cardStates,
    updateCardState,
    createChecklistMutation,
    createChecklistItemMutation,
  ]);

  if (checklists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">No checklists were extracted.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      {checklists.map((checklist, checklistIndex) => {
        const state = cardStates[checklistIndex];
        if (!state) return null;

        const selectedCount = state.selectedItems.size;

        return (
          <div
            key={checklistIndex}
            className={cn(
              "border border-border rounded-swivu-base overflow-hidden",
              state.created && "border-green-500/30 bg-green-500/5"
            )}
          >
            <button
              type="button"
              onClick={() => toggleExpanded(checklistIndex)}
              className="flex items-center gap-2 w-full px-3 py-2.5 hover:bg-muted/50 transition-colors"
            >
              {state.expanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <span className="text-sm font-medium text-foreground truncate flex-1 text-left">
                {checklist.title || "Untitled Checklist"}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {checklist.items.length} item{checklist.items.length !== 1 ? "s" : ""}
              </span>
              {state.created && (
                <Check className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
              )}
            </button>

            <AnimatePresence>
              {state.expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-border">
                    <div className="flex flex-col">
                      {checklist.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className={cn(
                            "flex items-start gap-2 px-3 py-2",
                            itemIndex < checklist.items.length - 1 && "border-b border-border/30"
                          )}
                        >
                          {!state.created && (
                            <input
                              type="checkbox"
                              checked={state.selectedItems.has(itemIndex)}
                              onChange={() => toggleItemSelection(checklistIndex, itemIndex)}
                              className="mt-0.5 rounded border-border accent-primary shrink-0"
                            />
                          )}
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className={cn(
                              "text-sm text-foreground",
                              state.created && !state.selectedItems.has(itemIndex) && "line-through text-muted-foreground"
                            )}>
                              {item.description}
                            </span>
                            {item.search_text && (
                              <div className="mt-1">
                                <SearchTextLink searchText={item.search_text} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="px-3 py-2 border-t border-border">
                      <Button
                        type="button"
                        variant={state.created ? "ghost" : "default"}
                        size="sm"
                        disabled={state.created || state.processing || selectedCount === 0}
                        onClick={() => handleCreateChecklist(checklist, checklistIndex)}
                        className={cn(
                          "w-full gap-2",
                          state.created && "text-green-600 dark:text-green-400"
                        )}
                      >
                        {state.created ? (
                          <>
                            <Check className="w-4 h-4" />
                            Checklist Created
                          </>
                        ) : (
                          <>
                            <ListChecks className="w-4 h-4" />
                            {state.processing
                              ? "Creating..."
                              : `Create Checklist (${selectedCount} item${selectedCount !== 1 ? "s" : ""})`}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
